import React from 'react';
import { useQuery } from '@apollo/client';
import { Text } from '@perch-labs/shrike';
import { Page } from 'styles';
import { useAccountContext } from 'contexts/account';
import { BackLink } from 'components/back-link';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { copyFor, routes } from 'config';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { getUtilityCredentialSubLabel } from 'domain/utility-credentials';
import { StandardRow } from 'components/standard-row';
import { DifferentUsernameAccordion } from 'pages/settings/components/different-username-accordion';
import { GET_UTILITY_SYNC_DATA } from './utility-sync.api';

const getCopy = copyFor('settings.utilitySync');
const { settings } = routes();

export const UtilitySync = () => {
  const { selectedAccountId } = useAccountContext();

  const { data, loading, error } = useQuery<
    GetUtilitySyncDataQuery,
    GetUtilitySyncDataQueryVariables
  >(GET_UTILITY_SYNC_DATA, {
    variables: { accountId: selectedAccountId },
  });

  if (loading) return <LoadingPage />;
  if (error || !data) {
    return <ErrorPage title={getCopy('title')} to={settings} />;
  }

  const { utilityCredentials } = data?.account || {};
  const showDifferentUsernameAccordion = utilityCredentials.some(
    credential => credential.utility.overrideEmailIsUsername
  );

  return (
    <>
      <BackLink to={settings} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text textStyle="heading800" tag="h1" margin={{ bottom: '48px' }}>
          {getCopy('title')}
        </Text>
        <Text
          tag="h2"
          textStyle="eyebrow400"
          color="secondary"
          margin={{ bottom: '16px' }}
        >
          {getCopy('subtitle')}
        </Text>
        {utilityCredentials.map((credential, index) => {
          const utilityName = credential.utility.displayName;
          const isIncorrectVerification =
            credential.verificationStatus === 'incorrect';
          const warningMessage = isIncorrectVerification
            ? getCopy('warningText')
            : null;
          return (
            <StandardRow
              key={`credential-for-sync-${credential.id}`}
              heading={
                <Text className="notranslate" tag="h3" textStyle="heading500">
                  {utilityName}
                </Text>
              }
              text={getUtilityCredentialSubLabel({
                utilityCredential: credential,
              })}
              warning={warningMessage}
              iconLinkProps={{
                ariaLabel: utilityName,
                margin: '0px',
                to: routes({ utilityCredentialId: credential.id })
                  .utilitySyncCredential,
              }}
              hasBottomBorder={index === utilityCredentials.length - 1}
            />
          );
        })}
      </Page>
      {showDifferentUsernameAccordion && <DifferentUsernameAccordion />}
    </>
  );
};
